import { ref, onMounted, onUnmounted } from 'vue'

/**
 * useNetworkStatus — reactive `online` flag + observable transitions.
 *
 * Reads navigator.onLine and listens to the browser's online/offline events.
 * Components can render an "offline" badge or block mutations when offline.
 *
 * Scope deliberately stops at *detection*. A persistent mutation-replay queue
 * (IndexedDB-backed, retry on reconnect) is a larger undertaking and was
 * intentionally removed in the recent sync refactor — re-introduce only when
 * the offline-first product story is firm. Until then, components can listen
 * to `onReconnect` to trigger a refetch of TanStack queries.
 *
 * Usage:
 *   const { online, onReconnect } = useNetworkStatus()
 *   onReconnect(() => queryClient.invalidateQueries())
 *
 *   <div v-if="!online" class="offline-banner">{{ t('common.offline') }}</div>
 */
const globalOnline = ref<boolean>(
  typeof navigator !== 'undefined' ? navigator.onLine : true
)

type Listener = () => void
const reconnectListeners = new Set<Listener>()
const offlineListeners = new Set<Listener>()
let listenersInstalled = false

function installListenersOnce() {
  if (listenersInstalled || typeof window === 'undefined') return
  listenersInstalled = true
  window.addEventListener('online', () => {
    globalOnline.value = true
    reconnectListeners.forEach((fn) => { try { fn() } catch { /* swallow */ } })
  })
  window.addEventListener('offline', () => {
    globalOnline.value = false
    offlineListeners.forEach((fn) => { try { fn() } catch { /* swallow */ } })
  })
}

export function useNetworkStatus() {
  installListenersOnce()

  function onReconnect(fn: Listener) {
    onMounted(() => reconnectListeners.add(fn))
    onUnmounted(() => reconnectListeners.delete(fn))
  }

  function onDisconnect(fn: Listener) {
    onMounted(() => offlineListeners.add(fn))
    onUnmounted(() => offlineListeners.delete(fn))
  }

  return { online: globalOnline, onReconnect, onDisconnect }
}
